import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Revalidator, RevalidatorOptions, SWRConfiguration } from 'swr';

import Loader from 'components/Common/Loader/Loader';
import PageNotFound from 'components/Common/Errors/PageNotFound/PageNotFound';
import ConfigurableProductView from 'components/ProductPageV2/ProductPage';
import GroupedProductView from 'components/GroupedProductView/GroupedProductView';
import { FetchError } from 'exceptions/FetchError';
import { useCustomSWR } from 'hooks/useCustomSWR';
import useEnv from 'hooks/useEnv';
import useScrollTop from 'hooks/useScrollTop';
import LargeSizeProvider from 'providers/LargeSizeProvider';
import { apiUrls } from 'services/api-urls';
import { fetcher } from 'services/fetcher';
import { LS_MODE_HASH } from 'settings/productV2';
import {
  ConfigurableProductPage,
  GroupedProductPage,
} from 'types/ProductPage/ProductPage';

import styles from './page.module.scss';

export type PageRef = {
  canChangeStaticPath: () => boolean;
};

export default function ProductPage(): JSX.Element {
  const { pathname, state } = useLocation();
  const { country } = useEnv();
  const componentRef = useRef<PageRef>(null);
  const [apiUrlState, setApiUrlState] = useState(pathname);
  const removeCountry = (url: string) => url.replace(`${country}/`, '');
  const { data, error } = useCustomSWR<
    ConfigurableProductPage | GroupedProductPage
  >(apiUrls.getProductStatic(removeCountry(apiUrlState)), fetcher, {
    fallbackData: state?.searchApiData?.staticData,
    onErrorRetry: onErrorRetryHandler,
  });

  useScrollTop();

  useEffect(() => {
    if (componentRef.current?.canChangeStaticPath?.() ?? true) {
      setApiUrlState(pathname);
    }
  }, [pathname]);

  if (error) {
    return (
      <div className={styles.notFoundContainer}>
        <PageNotFound />
      </div>
    );
  }

  if (!data) {
    return <Loader />;
  }

  return data.hasOwnProperty('linkedProducts') ? (
    <GroupedProductView data={data as GroupedProductPage} ref={componentRef} />
  ) : (
    <LargeSizeProvider
      isLargeMode={isLargeMode((data as ConfigurableProductPage).product)}
    >
      <ConfigurableProductView
        data={data as ConfigurableProductPage}
        ref={componentRef}
      />
    </LargeSizeProvider>
  );
}

function onErrorRetryHandler(
  error: FetchError,
  _key: string,
  config: SWRConfiguration,
  revalidate: Revalidator,
  { retryCount }: RevalidatorOptions
) {
  if (error.status === 404) {
    return;
  }

  setTimeout(() => revalidate({ retryCount }), config.errorRetryInterval);
}

function isLargeMode(product: ConfigurableProductPage['product']) {
  if (window.location.hash !== LS_MODE_HASH) return false;

  const {
    Attributes: { estGrandeTaille, imgFaceCurvy },
  } = product;

  return !!Number(estGrandeTaille.Value) && !!imgFaceCurvy?.Value.length;
}
